<template>
  <Card>
    <template #title>Call Log</template>
    <template #content>
      <div class="mb-3 text-sm text-gray-600">
        Showing calls for
        <span v-if="personId">
          person <code>{{ personId }}</code></span
        >
        <span v-else-if="organizationId">
          organization <code>{{ organizationId }}</code></span
        >
      </div>

      <div class="flex gap-2 mb-4">
        <Button
          v-if="phone"
          label="Call"
          icon="pi pi-phone"
          severity="success"
          @click="startCall"
        />
        <Button label="Log Call" icon="pi pi-plus" severity="info" @click="showDialog = true" />
      </div>

      <div v-if="callLogs.length === 0" class="text-sm text-gray-500">No calls logged yet.</div>

      <ul v-else class="space-y-2">
        <li v-for="log in callLogs" :key="log.id" class="border p-3 rounded-md">
          <div class="flex justify-between text-sm">
            <span :class="CallLogModel.getDirectionClass(log)">
              <i :class="['pi', CallLogModel.getDirectionIcon(log)]" class="mr-1"></i>
              {{ CallLogModel.formatDirection(log.direction) }} call
            </span>
            <span class="text-gray-500">{{ CallLogModel.formatTimestamp(log) }}</span>
          </div>
          <div class="mt-1 text-xs text-gray-600 whitespace-pre-line">
            {{ log.notes }}
          </div>
          <div v-if="log.tags?.length" class="mt-1 flex flex-wrap gap-2">
            <Tag
              v-for="tagId in log.tags"
              :key="tagId"
              :value="getTagLabel(tagId)"
              class="text-xs"
            />
          </div>
        </li>
      </ul>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import { useCallLogStore } from '@/stores/callLogStore'
import { useCallLogTagStore } from '@/stores/callLogTagStore'
import { useCallLogService } from '@/services/callLogService'
import { CallLogModel } from '@/models/CallLogModel'
import { usePhoneService } from '@/services/phoneService'

const props = defineProps<{
  personId?: string
  organizationId?: string
  phone?: string
}>()

const showDialog = ref(false)
const store = useCallLogStore()
const tagStore = useCallLogTagStore()
const callLogService = useCallLogService()
const phoneService = usePhoneService()

const callLogs = computed(() => store.items)

onMounted(async () => {
  tagStore.load()
  if (props.personId) {
    await callLogService.loadForPerson(props.personId)
  } else if (props.organizationId) {
    await callLogService.loadForOrganization(props.organizationId)
  }
})

function startCall() {
  if (props.phone) {
    const callUrl = phoneService.getCallUrl(props.phone)

    // Create a temporary link element for better mobile compatibility
    const link = document.createElement('a')
    link.href = callUrl
    link.target = '_self'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    showDialog.value = true
  }
}

function getTagLabel(id: string): string {
  return tagStore.tags.find((tag) => tag.id === id)?.label || id
}
</script>

<style scoped>
:deep(.p-dialog-content) {
  padding-bottom: 0;
}
</style>
